/**
 * designations.targets_roll — list of workspace electoral targets,
 * sorted by priority (top → low). Compact card surfacing who's
 * being unseated, with a link to the full /designations page.
 */
import { useQuery } from "@tanstack/react-query";
import { DataUnavailable } from "../../../framework/react/DataUnavailable.js";
import { Link } from "react-router-dom";
import { WidgetProps } from "../registry";
import { ArrowRight } from "lucide-react";
import { Skeleton } from "../../components/ui";

const PRIORITY_COLORS: Record<string, string> = {
  top: "bg-red-700 text-white",
  high: "bg-red-500 text-white",
  medium: "bg-orange-500 text-white",
  low: "bg-amber-200 text-amber-900",
};

export function TargetsRoll({ spec, workspaceId }: WidgetProps) {
  const limit = (spec.params?.limit as number) ?? 8;
  const cycle = spec.params?.cycle as string | undefined;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["widget", "targets_roll", workspaceId, cycle],
    queryFn: async () => {
      const params = new URLSearchParams({ status: "active" });
      if (cycle) params.set("cycle", cycle);
      const res = await fetch(`/api/v1/workspaces/${workspaceId}/electoral-targets?${params}`, { credentials: "include" });
      if (!res.ok) throw new Error("failed");
      return res.json();
    },
  });

  if (error) return <DataUnavailable message="Electoral targets could not be loaded." retry={refetch} />;
  if (isLoading) {
    return (
      <div role="status" className="space-y-2">
        <span className="sr-only">Loading…</span>
        <Skeleton className="h-3 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
        <Skeleton className="h-3 w-2/3" />
      </div>
    );
  }
  const targets: any[] = data?.targets ?? [];
  if (targets.length === 0) {
    return <div className="text-stone-500 text-xs">No electoral targets yet.</div>;
  }

  return (
    <div className="space-y-2 text-xs">
      <div className="flex items-baseline justify-between border-b border-stone-100 pb-1.5">
        <div className="text-stone-700 font-semibold">{data.total ?? targets.length} target{(data.total ?? targets.length) === 1 ? "" : "s"}</div>
        <Link className="inline-flex items-center gap-1 font-medium text-action hover:underline text-[11px]" to="/designations">View all <ArrowRight size={11} aria-hidden="true" /></Link>
      </div>
      <ul className="space-y-1">
        {targets.slice(0, limit).map((t: any) => {
          const m = t.target_meta || {};
          return (
            <li key={t.id} className="flex items-center gap-2">
              <span className={`tier-tag text-[9px] ${PRIORITY_COLORS[t.priority] || ""}`}>{t.priority}</span>
              <span className="flex-1 truncate text-stone-700">{m.display_name || m.role_title || "Unknown"}</span>
              <span className="font-mono text-stone-500 text-[10px]">
                {m.party?.[0] || "?"}-{m.state || ""}{m.district ? `-${m.district}` : ""}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
